import type { NavigateFunction } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
	createSessionCallAccessState,
	isSessionTranscriptionAllowed,
	isSessionTranscriptionMock,
	type SessionCallCompanyBanner,
} from '@/constants/sessionCall';
import { resolveVideoRoomApiError } from '@/components/session-call/sessionCallDailyJoin';
import { SessionService } from '@/services';
import { isP2PTranscriptionOnlySessionType } from '@/utils/sessionType';

type OpenProfessionalSessionCallOptions = {
	sessionId: string;
	navigate: NavigateFunction;
	sessionStatus: Sessions.SessionStatus;
	sessionType: Sessions.SessionType;
	isOnline?: boolean;
	transcriptionAllowed?: boolean;
	transcriptionMock?: boolean;
	companyBanner?: SessionCallCompanyBanner;
	onSessionStarted?: () => void;
	onStart?: () => void;
	onFinally?: () => void;
};

export async function openProfessionalSessionCall({
	sessionId,
	navigate,
	sessionStatus,
	sessionType,
	isOnline,
	transcriptionAllowed,
	transcriptionMock,
	companyBanner,
	onSessionStarted,
	onStart,
	onFinally,
}: OpenProfessionalSessionCallOptions): Promise<void> {
	onStart?.();

	try {
		let resolvedStatus = sessionStatus;

		if (resolvedStatus === 'CONFIRMED') {
			const startingToastId = toast.info('Iniciando sessão...', {
				autoClose: false,
				closeButton: false,
			});

			try {
				await SessionService.patchStart(sessionId);
				resolvedStatus = 'STARTED';
				onSessionStarted?.();
			} catch {
				toast.dismiss(startingToastId);
				toast.error(
					'Não foi possível iniciar a sessão. Tente novamente.',
				);
				return;
			}

			toast.dismiss(startingToastId);
		}

		if (isP2PTranscriptionOnlySessionType(sessionType)) {
			toast.info('Entrando na sessão...');
			navigate(`/profissional/sessao-chamada/${sessionId}`, {
				state: createSessionCallAccessState(
					'professional',
					companyBanner,
					resolvedStatus,
					undefined,
					sessionType,
					{
						isOnline: false,
						transcriptionAllowed,
						transcriptionMock,
					},
				),
			});
			return;
		}

		const loadingToastId = toast.info('Buscando reunião...', {
			autoClose: false,
			closeButton: false,
		});

		try {
			const credentials = await SessionService.getVideoRoom(sessionId);
			toast.dismiss(loadingToastId);
			toast.info('Entrando na reunião...');
			navigate(`/profissional/sessao-chamada/${sessionId}`, {
				state: createSessionCallAccessState(
					'professional',
					companyBanner,
					resolvedStatus,
					credentials,
					sessionType,
					{
						isOnline,
						transcriptionAllowed,
						transcriptionMock,
					},
				),
			});
		} catch (error) {
			toast.dismiss(loadingToastId);
			const apiError = resolveVideoRoomApiError(error);
			toast.error(
				apiError?.message ??
					'Não foi possível buscar a reunião. Tente novamente.',
			);
		}
	} finally {
		onFinally?.();
	}
}

export function resolveProfessionalSessionTranscriptionFlags(
	session: Pick<
		Sessions.Session,
		'transcription_allowed' | 'transcription_mock'
	>,
): {
	transcriptionAllowed: boolean;
	transcriptionMock: boolean;
} {
	return {
		transcriptionAllowed: isSessionTranscriptionAllowed(session),
		transcriptionMock: isSessionTranscriptionMock(session),
	};
}
